import {
  CurrencyAmount,
  DateRange,
  FlexibleDateRange,
  RateType,
  RoomLayoutType,
  TransferPartner,
} from "@/generated/search.openapi";
import {
  LoyaltyAccount,
  LoyaltyPerks,
  LoyaltyProgramEnum,
} from "@/generated/email_parser.openapi";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isMonday,
  isSaturday,
  nextSaturday,
  startOfMonth,
} from "date-fns";
import { Calendar, MessageSquare, Search, Sparkle } from "lucide-react";
import PointsIcon from "@/components/ui/svgComponents/PointsIcon";
import { DateRangeWithFlexibleData } from "@/app/(app)/[bookingType]/search/dateRangeWithFlexible/DateRangeWithFlexible";
import localFont from "next/font/local";
import {
  BedType,
  HotelData,
  TransferPartnersMapping,
} from "@/app/(app)/[bookingType]/search/context/searchProvider";
import {
  deriveProgramTierIndex,
  tierFromStatusName,
} from "@/lib/loyalty-tier.generated";

export {
  deriveProgramTierIndex,
  tierFromStatusName,
} from "@/lib/loyalty-tier.generated";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const DEFAULT_CASHBACK_PERCENTAGE = 0.03;

/**
 * Format spending amount for display in tier cards and gauges
 * Formats as "$Xk" or "$X.Yk" (removes .0 if decimal is zero)
 * Examples: $14,026 → "$14.0k", $14,000 → "$14k", $14,300 → "$14.3k"
 */
export const formatSpending = (amount: number): string => {
  if (amount >= 1000) {
    const valueInK = amount / 1000;
    // Round to 1 decimal place
    const rounded = Math.round(valueInK * 10) / 10;
    // Format with 1 decimal, then remove .0 if present
    const formatted = rounded.toFixed(1);
    return `$${formatted.replace(/\.0$/, "")}k`;
  }
  return `$${Math.round(amount)}`;
};

export function getSelectedLoyaltyAccount(
  loyaltyAccounts: LoyaltyAccount[],
  loyaltyProgram: string
) {
  return loyaltyAccounts
    .filter((account) => account.loyaltyProgram === loyaltyProgram)
    .sort((a, b) => {
      const pointsA = a.loyaltyPoints ?? -1; // Use nullish coalescing operator
      const pointsB = b.loyaltyPoints ?? -1; // Use nullish coalescing operator
      return pointsB - pointsA;
    })[0];
}

export function objectToUrlParams(obj: Record<string, string>) {
  return Object.entries(obj)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
}

export function diffTimeInHourAndMin(date1: Date, date2: Date) {
  const diffInMilliSeconds = Math.abs(date1.getTime() - date2.getTime());
  return `${Math.floor(diffInMilliSeconds / 3600000)} hr ${
    Math.floor(diffInMilliSeconds / 60000) % 60
  } min`;
}

export function calculateNumberOfNights(
  date1: Date | string,
  date2: Date | string
) {
  const d1 = getDateFormat(date1);
  const d2 = getDateFormat(date2);
  // Create new dates without time information
  const startDate = new Date(
    Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate())
  );
  const endDate = new Date(
    Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate())
  );

  const diffInMilliSeconds = Math.abs(startDate.getTime() - endDate.getTime());
  return Math.ceil(diffInMilliSeconds / 86400000);
}

export function camelToSnakeCase(camelStr: string) {
  return camelStr.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function snakeToCamelCase(snakeStr: string) {
  return snakeStr.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
}

export const convertLocalDateToUTC = (localDate: Date) => {
  const dateInUTC = new Date(`${format(localDate, "yyyy-MM-dd")}T00:00:00Z`);
  return dateInUTC;
};

export const convertUTCToLocalDateIgnoringTimezone = (
  _utcDate: Date | string
) => {
  const utcDate = getDateFormat(_utcDate);
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    utcDate.getUTCHours(),
    utcDate.getUTCMinutes(),
    utcDate.getUTCSeconds(),
    utcDate.getUTCMilliseconds()
  );
};

export const getDateFormat = (date?: string | Date) => {
  if (!date) return new Date();
  if (typeof date === "string") {
    // For ISO format strings like "YYYY-MM-DD", create date in local timezone without shifting
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(date);
  }
  return date;
};

/**
 * Creates a Date object at UTC noon for a given date string or Date object.
 * This ensures the date is stored correctly regardless of user's timezone,
 * avoiding date shifts when serialized to ISO strings.
 *
 * @param date - A date string (YYYY-MM-DD) or Date object
 * @returns A Date object set to noon UTC
 */
export const createDateAtUTCNoon = (date: string | Date): Date => {
  const dateObj = getDateFormat(date);
  return new Date(
    Date.UTC(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      12,
      0,
      0,
      0
    )
  );
};

/**
 * Extracts a YYYY-MM-DD date string from a Date object or ISO string.
 * Handles both Date objects and ISO strings (e.g., "2026-02-20T12:00:00.000Z").
 *
 * @param date - A Date object, ISO string, or YYYY-MM-DD string
 * @returns A YYYY-MM-DD formatted string, or null if date is invalid/undefined
 */
export const extractDateString = (
  date: Date | string | undefined
): string | null => {
  if (!date) return null;
  if (typeof date === "string") {
    // If it's an ISO string like "2026-02-20T12:00:00.000Z", extract date part
    if (date.includes("T")) {
      return date.split("T")[0];
    }
    // If it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
  } else {
    // Date object - extract YYYY-MM-DD using UTC to match API format
    return date.toISOString().split("T")[0];
  }
  return null;
};

export const updateHtmlListStyles = (
  instructions: string | string[]
): string | string[] => {
  if (typeof instructions === "string") {
    return [
      instructions
        .replace(/<ul>/g, '<ul class="list-disc list-outside pl-4">')
        .replace(/<ol>/g, '<ol class="list-decimal list-outside pl-4">'),
    ];
  }
  return instructions?.map((instruction) => {
    if (!instruction) return "";
    return instruction
      .replace(/<ul>/g, '<ul class="list-disc list-outside pl-4">')
      .replace(/<ol>/g, '<ol class="list-decimal list-outside pl-4">');
  });
};

export const camelCaseToHumanReadable = (camelCaseString: string) => {
  return camelCaseString
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
};

export const snakeCaseToHumanReadable = (snakeCaseString: string) => {
  return snakeCaseString
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase());
};

export const convertJsonToUrlParams = (
  queryParams: Record<string, string | number | boolean>
) => {
  return (
    "?" +
    encodeURI(
      Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join("&")
    )
  );
};

export const formatNumber = (
  number: number,
  maximumFractionDigits: number = 2,
  round: "up" | "down" | "nearest" = "nearest"
) => {
  const factor = Math.pow(10, maximumFractionDigits);
  switch (round) {
    case "up":
      number = Math.ceil(number * factor) / factor;
      break;
    case "down":
      number = Math.floor(number * factor) / factor;
      break;
    case "nearest":
    default:
      number = Math.round(number * factor) / factor;
  }
  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(
    number
  );
};

export const formatCurrency = (
  amount: string | number,
  currency: string = "USD",
  maximumFractionDigits: number = 2,
  round: "up" | "down" | "nearest" = "nearest"
) => {
  let _amount;
  if (typeof amount === "string") {
    _amount = Number(amount);
  } else {
    _amount = amount;
  }

  // Validate the currency code
  const validCurrency = isValidCurrency(currency) ? currency : "USD";

  const factor = Math.pow(10, maximumFractionDigits);
  switch (round) {
    case "up":
      _amount = Math.ceil(_amount * factor) / factor;
      break;
    case "down":
      _amount = Math.floor(_amount * factor) / factor;
      break;
    case "nearest":
    default:
      _amount = Math.round(_amount * factor) / factor;
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: validCurrency,
    maximumFractionDigits: maximumFractionDigits,
  }).format(_amount);
};

/**
 * Clamps a Gondola Cash balance to a floor of $0 for display.
 *
 * A user's Gondola Cash balance can go negative (e.g. when cashback that was
 * already spent gets clawed back after a source booking is cancelled). We never
 * surface a negative balance to users — show $0 instead. This is display-only:
 * payment-eligibility and top-up math still use the real (possibly negative)
 * value.
 */
export const getDisplayGondolaCashValue = (
  value: number | null | undefined
): number => Math.max(0, value ?? 0);

export const convertRawCurrencyToString = (
  currenctAmt: CurrencyAmount,
  isLocalized: boolean = false,
  maximumFractionDigits: number = 2
) => {
  if (currenctAmt?.currency === undefined || currenctAmt?.currency === null) {
    return currenctAmt?.value ? `${currenctAmt?.value}` : "";
  }
  if (currenctAmt?.currency === "points") {
    return `${Intl.NumberFormat("en-US").format(currenctAmt.value)}`;
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: isLocalized
      ? currenctAmt.localizedAmount?.currency
      : currenctAmt.currency,
    maximumFractionDigits: maximumFractionDigits,
  }).format(
    isLocalized
      ? (currenctAmt.localizedAmount?.value as number)
      : currenctAmt.value
  );
};

// Helper function to check if the currency code is valid
const isValidCurrency = (currency: string) => {
  try {
    new Intl.NumberFormat(undefined, { style: "currency", currency }).format(0);
    return true;
  } catch {
    return false;
  }
};

export const imgCategoriesMapping: {
  [key: string]: string;
} = {
  pool: "/assets/image-category/pool.svg",
  lobby: "/assets/image-category/lobby.svg",
  spa: "/assets/image-category/spa.svg",
  exterior: "/assets/image-category/exterior.svg",
  room: "/assets/image-category/room.svg",
  "fitness center": "/assets/image-category/fitness-center.svg",
  views: "/assets/image-category/views.svg",
  "food & drink": "/assets/image-category/food-drink.svg",
  casino: "/assets/image-category/casino.svg",
};

export const replaceUrl = (newUrl: string) => {
  window.history.replaceState(
    { ...window.history.state, as: newUrl, url: newUrl },
    "",
    newUrl
  );
};

export const capitalizeFirstLetter = (str: string | undefined | null) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalizeEachWord = (str: string | undefined | null) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const screenInnerWidth =
  typeof window !== "undefined" ? window.innerWidth : 0;

export const screenInnerHeight =
  typeof window !== "undefined" ? window.innerHeight : 0;

export const sortHotelByPointPercentile = (a: HotelData, b: HotelData) => {
  const aPercentile = a.pointsPercentile;
  const bPercentile = b.pointsPercentile;
  if (aPercentile === undefined || aPercentile === 0) {
    return bPercentile === undefined || bPercentile === 0 ? 0 : 1;
  }
  return aPercentile && bPercentile ? bPercentile - aPercentile : -1;
};

export const sortHotelByCashPercentile = (a: HotelData, b: HotelData) => {
  const aPercentile = a.cashPercentile;
  const bPercentile = b.cashPercentile;
  if (aPercentile === undefined || aPercentile === 0) {
    return bPercentile === undefined || bPercentile === 0 ? 0 : 1;
  }
  return bPercentile && aPercentile ? aPercentile - bPercentile : -1;
};

export const sortHotelByPastStaysSimilarity = (a: HotelData, b: HotelData) => {
  const aPastStaysSimilarity = a.recommendationSummary?.pastStaysSimilarity;
  const bPastStaysSimilarity = b.recommendationSummary?.pastStaysSimilarity;
  if (aPastStaysSimilarity === undefined || aPastStaysSimilarity === 0) {
    return bPastStaysSimilarity === undefined || bPastStaysSimilarity === 0
      ? 0
      : 1;
  }
  return bPastStaysSimilarity && aPastStaysSimilarity
    ? bPastStaysSimilarity - aPastStaysSimilarity
    : -1;
};

export const xlBreakPoint = 1350;
export const lgBreakPoint = 1100;
export const mdBreakPoint = 768;
export const smallHeightBreakPoint = 750;

export const detectWebviewOrInAppBrowser = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();

  // ---------- 1. Detect REAL WebViews (most reliable) ----------

  // iOS WebView: Safari missing
  const isIOS = /iphone|ipod|ipad/.test(ua);
  const isIOSWebView = isIOS && /applewebkit/.test(ua) && !/safari/.test(ua);

  // Android WebView: "; wv" or Version/X.X
  const isAndroidWebView =
    /android/.test(ua) && (/\bwv\b/.test(ua) || /version\/[\d.]+/.test(ua));

  // ---------- 2. Detect major IN-APP browsers ----------

  // Facebook App or Messenger
  const isFacebook = ua.includes("fbav") || ua.includes("fb_iab");

  // Instagram
  const isInstagram = ua.includes("instagram");

  // TikTok
  const isTikTok = ua.includes("tiktok");

  // Snapchat
  const isSnapchat = ua.includes("snapchat");

  // Twitter / X
  const isTwitter = ua.includes("twitter");

  // ---------- 3. Feature-based heuristics ----------

  const isWebview = Boolean(
    isIOSWebView ||
      isAndroidWebView ||
      isFacebook ||
      isInstagram ||
      isTikTok ||
      isSnapchat ||
      isTwitter
  );
  // ---------- 4. Final decision ----------
  return isWebview;
};

export const getRating = (rating: number) => {
  return (rating * 2).toFixed(1);
};

export const getAllPossibleNightsForAMonth = (
  selectedMonth: Date,
  nights: number,
  onlyFutureDates: boolean = true
): DateRange[] => {
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(monthStart);

  const allDates = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  const nightsRanges: DateRange[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < allDates.length; i++) {
    const date = allDates[i];
    if (onlyFutureDates && date < today) continue;
    const endDate = addDays(date, nights);
    if (endDate <= monthEnd) {
      nightsRanges.push({
        startDate: date,
        startDateStr: format(date, "yyyy-MM-dd"),
        endDate,
        endDateStr: format(endDate, "yyyy-MM-dd"),
      });
    }
  }

  return nightsRanges;
};

const getWeekDayDatesForAMonth = (selectedMonth: Date): DateRange[] => {
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(monthStart);

  const allDates = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  const weekdays: DateRange[] = [];

  for (let i = 0; i < allDates.length; i++) {
    const date = allDates[i];
    if (isMonday(date)) {
      weekdays.push({
        startDate: date,
        startDateStr: format(date, "yyyy-MM-dd"),
        endDate: addDays(date, 4),
        endDateStr: format(addDays(date, 4), "yyyy-MM-dd"),
      });
    }
  }

  return weekdays;
};

export const getNextWeekWeekendFromNow = (): DateRange => {
  const now = new Date();
  const isTodaySaturday = isSaturday(now);
  if (isTodaySaturday) {
    const nextSat = nextSaturday(now);
    const nextSun = addDays(nextSat, 1);
    return { startDate: nextSat, endDate: nextSun };
  }
  const nextSat = nextSaturday(addDays(now, 7));
  const nextSun = addDays(nextSat, 1);
  return { startDate: nextSat, endDate: nextSun };
};

// const getWeekendDatesForAMonth = (selectedMonth: Date): DateRange[] => {
//   const monthStart = startOfMonth(selectedMonth);
//   const monthEnd = endOfMonth(monthStart);

//   const allDates = eachDayOfInterval({
//     start: monthStart,
//     end: monthEnd,
//   });

//   const weekends: DateRange[] = [];

//   for (let i = 0; i < allDates.length; i++) {
//     const date = allDates[i];
//     if (isSaturday(date)) {
//       const sunday = allDates[i + 1];
//       if (sunday && isSunday(sunday)) {
//         weekends.push({ startDate: date, endDate: sunday });
//         i++;
//       }
//     }
//   }

//   return weekends;
// };

export const getWeekendDatesForAMonthFriAndSat = (
  selectedMonth: Date,
  onlyFutureDates: boolean = true
): DateRange[] => {
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(monthStart);

  const allDates = eachDayOfInterval({
    start: monthStart,
    end: monthEnd,
  });

  const weekends: DateRange[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < allDates.length; i++) {
    const date = allDates[i];
    if (isSaturday(date)) {
      const friday = allDates[i - 1];
      const sunday = allDates[i + 1];
      if (friday && sunday) {
        if (onlyFutureDates && friday < today) continue;
        weekends.push({
          startDate: friday,
          startDateStr: format(friday, "yyyy-MM-dd"),
          endDate: sunday,
          endDateStr: format(sunday, "yyyy-MM-dd"),
        });
      }
    }
  }

  return weekends;
};

export const defaultInitialPageRoute = "/hotel/search";

export const navItems = [
  {
    key: "hotel-search",
    title: "Search",
    href: "/hotel/search",
    icon: <Search className="mr-2 h-4 w-4" />,
  },
  {
    key: "points",
    title: "Points",
    href: "/loyalty-dashboard",
    icon: <PointsIcon className="mr-2 h-4 w-4" />,
  },
  {
    key: "trips",
    title: "Trips",
    href: "/my-trips",
    icon: <Calendar className="mr-2 h-4 w-4" />,
  },

  {
    key: "explore",
    title: "Explore",
    href: "/explore",
    icon: <Sparkle className="mr-2 h-4 w-4" />,
  },
  {
    key: "concierge",
    title: "Concierge",
    href: "/concierge",
    icon: <MessageSquare className="mr-2 h-4 w-4" />,
  },
];

type SocialMediaIcon = {
  link: string;
  image: string;
  imageTransparent: string;
  alt: string;
  name: string;
};

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const isValidEmail = (email: string) => {
  const trimmedEmail = email.trim();
  if (!EMAIL_PATTERN.test(trimmedEmail)) return false;

  const [localPart, domain, extraPart] = trimmedEmail.split("@");
  if (!localPart || !domain || extraPart) return false;
  if (
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..")
  ) {
    return false;
  }
  if (domain.startsWith(".") || domain.endsWith(".") || domain.includes("..")) {
    return false;
  }

  return domain
    .split(".")
    .every(
      (label) =>
        label.length > 0 && !label.startsWith("-") && !label.endsWith("-")
    );
};

// Detects plus-addressed (sub-addressed) emails, e.g. "user+tag@example.com".
// Marriott's direct-booking checkout rejects a "+" in the local part with
// error code 244 "Invalid Email Address" (HTTP 422), so we block these before
// submitting a hotel booking. Only the local part (before "@") is checked.
export const isPlusAddressedEmail = (email: string): boolean => {
  if (!email) return false;
  const localPart = email.split("@")[0] ?? "";
  return localPart.includes("+");
};

// Validates an international or domestic phone number.
// Accepts optional leading +, digits, spaces, dashes, dots, and parentheses.
// Requires at least 7 digits after stripping formatting.
export const isValidPhone = (phone: string): boolean => {
  if (!phone || !phone.trim()) return false;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

export const isValidName = (name: string): boolean => {
  if (!name || !name.trim()) return false;
  // Allow letters, spaces, hyphens, and apostrophes (e.g. Mary-Jane, O'Brien)
  // Hyphens are sanitized out before reaching Travelport on the backend
  // Must contain at least one letter (rejects "---", "'''" etc.)
  const trimmed = name.trim();
  return /^[a-zA-Z\s\-']+$/.test(trimmed) && /[a-zA-Z]/.test(trimmed);
};

export const isNumber = (value: string | number) => {
  const num = typeof value === "number" ? `${value}` : value;
  return new RegExp("^[0-9]+$").test(num);
};
export const socialMediaIcons: SocialMediaIcon[] = [
  {
    name: "instagram",
    link: "https://www.instagram.com/gondola_ai",
    image: "/assets/icons/instagram.svg",
    imageTransparent: "/assets/icons/instagram-transparent.svg",
    alt: "instagram",
  },
  {
    name: "linkedin",
    link: "https://www.linkedin.com/company/gondola-ai-travel",
    image: "/assets/icons/linkedin.svg",
    imageTransparent: "/assets/icons/linkedin-transparent.svg",
    alt: "linkedin",
  },
];

export const getFlexiableDatesForNights = (
  data: DateRangeWithFlexibleData
): FlexibleDateRange | undefined => {
  if (!data.flexible) return;
  const {
    flexible: { selectedMonth, nights },
  } = data;
  const startOfMonthDate = startOfMonth(selectedMonth);
  const endOfMonthDate = endOfMonth(selectedMonth);
  return {
    startDate: startOfMonthDate,
    startDateStr: format(startOfMonthDate, "yyyy-MM-dd"),
    endDate: endOfMonthDate,
    endDateStr: format(endOfMonthDate, "yyyy-MM-dd"),
    nights,
  };
};

export const getFlexiableDates = (
  data: DateRangeWithFlexibleData
): DateRange[] => {
  if (!data.flexible) return [];
  const {
    flexible: { selectedMonth, stayLength },
  } = data;
  if (stayLength === "Weekend") {
    return getWeekendDatesForAMonthFriAndSat(selectedMonth);
  }
  return getWeekDayDatesForAMonth(selectedMonth);
};

export const getTransferPartnersMapping = (
  availableTransferPartners: TransferPartner[]
): TransferPartnersMapping => {
  const mapping = {} as { [key: string]: LoyaltyProgramEnum[] };
  availableTransferPartners.forEach((partner) => {
    mapping[partner.fromLoyaltyProgram]
      ? mapping[partner.fromLoyaltyProgram].push(
          partner.toLoyaltyProgram as LoyaltyProgramEnum
        )
      : (mapping[partner.fromLoyaltyProgram] = [
          partner.toLoyaltyProgram as LoyaltyProgramEnum,
        ]);
  });
  return mapping;
};

export const getTransferPartners = (
  loyaltyProgram: string,
  availableTransferPartners: TransferPartner[]
) =>
  getTransferPartnersFromLoyalty(loyaltyProgram, availableTransferPartners).map(
    (partner) => {
      const lp = formatCapsText(partner.fromLoyaltyProgram);
      if (lp === "American Express") return "Amex";
      return lp;
    }
  );

export const getTransferPartnersFromLoyalty = (
  loyaltyProgramName: string,
  availableTransferPartners: TransferPartner[]
) => {
  return availableTransferPartners.filter(
    (transferPartners) =>
      transferPartners.toLoyaltyProgram === loyaltyProgramName
  );
};

// For both card backgrounds and tag pills, prefer the status name over the
// chain's numeric tier — a status that says "SILVER" should always look silver,
// even when the chain numbers it differently (e.g. Accor SILVER = index 3).
//
// `loyaltyProgram` (when provided) is prepended to the status before keyword
// matching so chain-specific overrides can disambiguate names that collide
// with generic keywords. Required for Enterprise Plus Silver/Gold/Platinum to
// avoid the generic "platinum"/"gold"/"silver" rules over-promoting them.
const resolveLoyaltyTier = (
  index: number | undefined,
  loyaltyStatus?: string | null,
  loyaltyProgram?: string | null
): number =>
  tierFromStatusName(loyaltyStatus, loyaltyProgram) ??
  deriveProgramTierIndex(index, loyaltyStatus, loyaltyProgram);

export const getLoyaltyCardBGFromLoyaltyIndex = (
  index?: number,
  loyaltyStatus?: string | null,
  loyaltyProgram?: string | null
) => {
  const tier = resolveLoyaltyTier(index, loyaltyStatus, loyaltyProgram);
  switch (true) {
    case tier === 0:
      return "bg-card";
    case tier === 1:
      return "card-linear-gradient-bronze";
    case tier === 2:
      return "card-linear-gradient-default";
    case tier === 3:
      return "card-linear-gradient-gold";
    case tier === 4:
      return "card-linear-gradient-titanium";
    case tier === 5:
      return "card-linear-gradient-platinum";
    default:
      return "bg-card";
  }
};

export const getLoyaltyTagFromLoyaltyIndex = (
  index?: number,
  loyaltyStatus?: string | null,
  loyaltyProgram?: string | null
) => {
  const tier = resolveLoyaltyTier(index, loyaltyStatus, loyaltyProgram);
  switch (true) {
    case tier === 1:
      return "loyalty-tag-bronze";
    case tier === 2:
      return "loyalty-tag-silver";
    case tier === 3:
      return "loyalty-tag-gold";
    case tier === 4:
      return "loyalty-tag-titanium";
    case tier === 5:
      return "loyalty-tag-platinum";
    default:
      return "loyalty-tag-default";
  }
};

export const getPerksFromLoyalty = (loyalty: LoyaltyAccount) => {
  if (!loyalty.loyaltyPerks) return null;
  return Object.entries(loyalty.loyaltyPerks)
    .filter(
      ([key, value]) => key.toLowerCase().includes("perks") && Boolean(value)
    )
    .map(([key, value]) => ({
      icon: `/assets/loyalty-perks/${camelToSnakeCase(key)}.svg`,
      text: value,
    }));
};

export const getPerksFromLoyaltyPerks = (loyaltyPerks: LoyaltyPerks) => {
  return Object.entries(loyaltyPerks)
    .filter(
      ([key, value]) => key.toLowerCase().includes("perks") && Boolean(value)
    )
    .map(([key, value]) => ({
      icon: `/assets/loyalty-perks/${camelToSnakeCase(key)}.svg`,
      text: value,
    }));
};

export const scrollSmoothToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const institutionCodes = [
  "amex",
  "bank_of_america",
  "barclaycardus",
  "chase",
  "citibank",
  "us_bank",
];

// TODO: This way of getting institution code is not reliable, need to find a better way OR add a field in the loyalty account object
export const getInstitutionCodeFromProgramName = (programName: string) => {
  const arr = programName.split(" ").map((word) => word.toLowerCase());
  const code = institutionCodes.find((code) => arr.includes(code));
  return code || null;
};

export const formatCapsText = (text: string) => {
  return text
    .toLowerCase()
    .split(/[_ ]+/)
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

export const getCountryName = (countryCode: string | null | undefined) => {
  if (!countryCode || countryCode.trim() === "") {
    return "";
  }

  try {
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    const result = regionNames.of(countryCode);
    return result || countryCode; // Fallback to original code if display name is not available
  } catch (error) {
    // If DisplayNames fails, return the original country code
    return countryCode;
  }
};
export const getNumberPostfix = (number: number): string => {
  const lastDigit = number % 10;
  const lastTwoDigits = number % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return "th";
  }

  switch (lastDigit) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const ppeikoFont = localFont({
  src: [
    {
      path: "../public/fonts/PPEiko-Medium.woff2",
      weight: "500",
      style: "normal",
    },
    {
      path: "../public/fonts/PPEiko-MediumItalic.woff2",
      weight: "500",
      style: "italic",
    },
  ],
  display: "swap",
  preload: false,
});

export function getTodayString(): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Returns true if the stay is in the past (checkout date is before today).
 * Uses local date-only comparison via getDateFormat (no time component) to avoid
 * timezone and daylight-saving off-by-one issues.
 *
 * When checkout date is missing (undefined or empty), returns false so that
 * incomplete or still-processing bookings remain cancellable until we have
 * a checkout date that confirms the stay is in the past.
 *
 * @param checkoutStr - YYYY-MM-DD string or undefined (missing = treat as not past, allow cancellation)
 * @param todayStr - YYYY-MM-DD string for "today" (e.g. from getTodayString())
 */
export function isStayInThePast(
  checkoutStr: string | undefined,
  todayStr: string
): boolean {
  if (!checkoutStr) return false; // Allow cancellation when checkout date unknown (incomplete/processing booking)
  const checkoutDate = getDateFormat(checkoutStr);
  const todayAtMidnight = getDateFormat(todayStr);
  return checkoutDate < todayAtMidnight;
}

export function isTravelportOrDirectRate(rate: {
  rateType: RateType;
}): boolean {
  return (
    rate.rateType === RateType.Travelport ||
    rate.rateType === RateType.TravelportPromotional ||
    rate.rateType === RateType.TravelportMember ||
    rate.rateType === RateType.TravelportAaa ||
    rate.rateType === RateType.TravelportAarp ||
    rate.rateType === RateType.TravelportSenior ||
    rate.rateType === RateType.TravelportGovernment ||
    rate.rateType === RateType.TravelportMilitary ||
    rate.rateType === RateType.TravelportBookingCom ||
    rate.rateType === RateType.TravelportPackage ||
    rate.rateType === RateType.TravelportAbcVip ||
    rate.rateType === RateType.TravelportAbc ||
    rate.rateType === RateType.DirectCash ||
    rate.rateType === RateType.DirectPoints
  );
}

// Helper to find a matching loyalty account by email and program
export function findMatchingLoyaltyAccount(
  emailAddress: string | undefined,
  loyaltyAccounts: LoyaltyAccount[],
  loyaltyProgramEnum: string | undefined
): LoyaltyAccount | undefined {
  if (!loyaltyProgramEnum) return undefined;

  // First try to find a matching account by email
  const matchingByEmail = loyaltyAccounts.find(
    (acc) =>
      acc.account?.email === emailAddress &&
      acc.loyaltyProgram === loyaltyProgramEnum
  );

  // If no matching account by email, look for any account for this chain
  if (!matchingByEmail) {
    return loyaltyAccounts.find(
      (acc) => acc.loyaltyProgram === loyaltyProgramEnum
    );
  }

  return matchingByEmail;
}

/**
 * Get all matching loyalty accounts for a specific loyalty program
 *
 * @param loyaltyAccounts - Array of user's loyalty accounts
 * @param loyaltyProgramEnum - The loyalty program enum for the hotel chain
 * @returns Array of matching loyalty accounts
 */
export function getMatchingLoyaltyAccounts(
  loyaltyAccounts: LoyaltyAccount[],
  loyaltyProgramEnum: string | undefined
): LoyaltyAccount[] {
  if (!loyaltyProgramEnum) return [];
  return loyaltyAccounts.filter(
    (acc) => acc.loyaltyProgram === loyaltyProgramEnum
  );
}

export function getFilterRateKey(
  roomType?: RoomLayoutType,
  bedType?: BedType
): string | undefined {
  // Room type takes precedence over bed type when both are set
  if (roomType) return roomType.toLowerCase();
  if (bedType) {
    const map: Record<BedType, string> = {
      "1 king": "1_king",
      "1 queen": "1_queen",
      "2 queens": "2_queens",
      "2 double beds": "2_double_beds",
      "2 full beds": "2_double_beds",
    };
    return map[bedType];
  }
  return undefined;
}

export const getDisplayNightlyRate = (
  hotel: HotelData,
  isRefundable?: boolean,
  roomType?: RoomLayoutType,
  bedType?: BedType
) => {
  const filterKey = getFilterRateKey(roomType, bedType);
  if (filterKey) {
    const filterRate = hotel.lowestRatesByFilter?.[filterKey]?.nightly?.value;
    if (filterRate != null) return filterRate;
  }
  return isRefundable
    ? hotel.refundableNightlyRate?.value
    : hotel.bestNightlyRate?.value;
};

export const getDisplayTotalCost = (
  hotel: HotelData,
  isRefundable?: boolean,
  roomType?: RoomLayoutType,
  bedType?: BedType
) => {
  const filterKey = getFilterRateKey(roomType, bedType);
  if (filterKey) {
    const filterRate = hotel.lowestRatesByFilter?.[filterKey]?.total?.value;
    if (filterRate != null) return filterRate;
  }
  return isRefundable
    ? hotel.lowestRefundableRate?.value
    : hotel.bestRate?.value;
};

/**
 * Sanitizes payment method input to contain only alphanumeric characters and spaces.
 *
 * Security-focused sanitization that removes all special characters, Unicode characters,
 * HTML tags, and potentially dangerous patterns to prevent injection attacks.
 *
 * Special characters are replaced with spaces for better readability, preserving word boundaries
 * in names and addresses (e.g., "O'Connor" → "O Connor", "Whitchurch-Stouffville" → "Whitchurch Stouffville").
 *
 * @param input - The input string to sanitize
 * @returns Sanitized string containing only ASCII letters (a-z, A-Z), numbers (0-9), and spaces.
 *          Leading/trailing spaces are preserved. Empty string if input is null/undefined.
 *
 * @note Does not validate length - caller should enforce max length constraints
 * @note Accented Latin letters are folded to their ASCII base (e.g., "Québec" → "Quebec", "José" → "Jose"). Characters with no ASCII equivalent (non-Latin scripts, emojis) are dropped.
 * @note Special characters (apostrophes, hyphens, periods, commas, slashes, parentheses) are replaced with spaces
 *
 * @example
 * sanitizePaymentInput("John Smith") // "John Smith"
 * @example
 * sanitizePaymentInput("O'Connor") // "O Connor"
 * @example
 * sanitizePaymentInput("Whitchurch-Stouffville") // "Whitchurch Stouffville"
 * @example
 * sanitizePaymentInput("Dr. Smith Jr.") // "Dr Smith Jr"
 * @example
 * sanitizePaymentInput("123 Main St.") // "123 Main St"
 * @example
 * sanitizePaymentInput("<script>alert('xss')</script>") // ""
 */
export function sanitizePaymentInput(input: string): string {
  if (!input) return "";

  // Early removal of angle brackets to prevent ReDoS attacks
  // This is more performant than complex regex patterns
  if (input.includes("<") || input.includes(">")) {
    // Remove angle brackets and everything between them (handles HTML/script tags)
    input = input.replace(/<[^>]*>/g, "");
  }

  // Remove event handlers (onclick, onerror, etc.) - simple pattern to avoid ReDoS
  let sanitized = input
    .replace(/on\w+\s*=/gi, "")
    // Fold accented Latin letters to their ASCII base (é → e, ç → c, ü → u)
    // by decomposing to base + combining mark, then dropping the marks.
    // This must run before the ASCII strip below, otherwise a composed
    // codepoint like "é" (U+00E9) is removed wholesale ("Québec" → "Qubec").
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Remove any remaining non-ASCII characters (non-Latin scripts, emojis,
    // control/zero-width characters) that have no ASCII equivalent.
    .replace(/[^\x20-\x7E]/g, ""); // Only keep printable ASCII (space through ~)

  // Replace common special characters with spaces for better readability
  // This preserves word boundaries in names and addresses
  sanitized = sanitized
    .replace(/'/g, " ") // Apostrophes: "O'Connor" → "O Connor"
    .replace(/-/g, " ") // Hyphens: "Mary-Jane" → "Mary Jane"
    .replace(/\./g, " ") // Periods: "Dr. Smith" → "Dr Smith"
    .replace(/,/g, " ") // Commas: "Smith, John" → "Smith John"
    .replace(/\//g, " ") // Slashes: "Apt 5/6" → "Apt 5 6"
    .replace(/\(/g, " ") // Opening parentheses
    .replace(/\)/g, " "); // Closing parentheses

  // Final pass: keep only alphanumeric and spaces
  // This removes any remaining special characters that passed through
  sanitized = sanitized.replace(/[^a-zA-Z0-9 ]/g, "");

  // Normalize multiple consecutive spaces to single space
  sanitized = sanitized.replace(/\s+/g, " ");

  return sanitized;
}

/**
 * Format cardholder name from first and last name
 */
export function formatCardholderName(
  firstName?: string,
  lastName?: string
): string {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "";
}

/**
 * Format billing address for display as a single line
 */
export function formatBillingAddress(
  line1?: string,
  line2?: string,
  city?: string,
  state?: string,
  zip?: string
): string {
  const parts: string[] = [];

  // Add street address lines
  if (line1?.trim()) {
    parts.push(line1.trim());
  }
  if (line2?.trim()) {
    parts.push(line2.trim());
  }

  // Add city, state, zip
  const cityStateZip = [city, state, zip]
    .filter(Boolean)
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(", ");
  if (cityStateZip) {
    parts.push(cityStateZip);
  }

  return parts.join(", ");
}

/**
 * Normalize email for comparison (lowercase and trim)
 * This helps prevent duplicate travel profiles due to case sensitivity or whitespace
 */
export function normalizeEmail(email: string | null | undefined): string {
  if (!email) return "";
  return email.trim().toLowerCase();
}

/** Optional room context used to detect supplier rate names that are really
 * just a fragment of the room name/description (see below). */
export interface SupplierRateNameRoomContext {
  roomName?: string | null;
  roomDescription?: string | null;
}

/**
 * Returns a supplier rate name suitable for display, or null if it should be hidden.
 *
 * Suppliers (notably Travelport) frequently return junk in this field:
 * - Long freeform terms & conditions blobs.
 * - Supplier disclaimers containing "important".
 * - A bare number with no real word (e.g. "1").
 * - A slice of the room description mislabeled as a rate name
 *   (e.g. "1 mini fridge mi" for a "...Mini Fridge, Mi..." room). When
 *   `roomContext` is supplied, a candidate whose every significant word is
 *   already present in the room name/description is treated as such a fragment.
 */
export function getDisplayableSupplierRateName(
  supplierRateName: string | null | undefined,
  roomContext?: SupplierRateNameRoomContext
): string | null {
  if (!supplierRateName) {
    return null;
  }
  const trimmed = supplierRateName.trim();
  if (
    !trimmed ||
    trimmed.length > 50 ||
    trimmed.toLowerCase().includes("important")
  ) {
    return null;
  }

  // Significant words: alphabetic tokens of length >= 2. A candidate with no
  // such word (a bare number like "1") is not a meaningful label.
  const lower = trimmed.toLowerCase();
  const words = lower
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 2 && /[a-z]/.test(w));
  if (words.length === 0) {
    return null;
  }

  // Description fragment: if every significant word already appears in the room
  // name/description, this is a slice of the room text, not a rate name. Match
  // on whole words (not substrings) so short tokens like "it" aren't counted as
  // present just because they sit inside "suite"/"with".
  const roomWords = new Set(
    `${roomContext?.roomName ?? ""} ${roomContext?.roomDescription ?? ""}`
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
  );
  if (roomWords.size > 0 && words.every((w) => roomWords.has(w))) {
    return null;
  }

  return capitalizeFirstLetter(trimmed.toLocaleLowerCase());
}

// Transfer ratios arrive as hotel points per card point (Amex to Hilton 2,
// Sapphire Preferred to Hyatt 0.75, Amex to Leaders Club 0.25). Render the
// smallest whole-number pair as card:hotel (1:2, 4:3, 4:1) so no partner shows
// a fraction; fall back to 1:N with two decimals when no pair under 100 fits.
export function formatTransferRatio(hotelPointsPerCardPoint: number): string {
  if (!Number.isFinite(hotelPointsPerCardPoint) || hotelPointsPerCardPoint <= 0)
    return "—";
  for (let cardPoints = 1; cardPoints <= 100; cardPoints++) {
    const hotelPoints = Math.round(hotelPointsPerCardPoint * cardPoints);
    if (
      hotelPoints > 0 &&
      Math.abs(hotelPoints / cardPoints - hotelPointsPerCardPoint) <= 0.0001
    ) {
      return `${cardPoints}:${hotelPoints}`;
    }
  }
  const fallback =
    Number(hotelPointsPerCardPoint.toFixed(2)) ||
    Number(hotelPointsPerCardPoint.toPrecision(2));
  return `1:${fallback}`;
}
